// Fluidez 12M — gráfico de cashflow acumulado ao longo de 12 meses.
// Respeita `payment_terms` (30/60/90 dias) — o cliente paga N dias após o serviço,
// pelo que a empresa tem "gap" de tesouraria nos primeiros meses.

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { formatCurrency } from "./helpers";

const MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/**
 * Constrói a série de cashflow acumulado para 12 meses.
 *
 * Regra:
 *  - Custo mensal (`monthlyCost`) sai da tesouraria TODOS os meses.
 *  - Receita (`monthlyRevenue`) só entra `payment_delay_months` meses depois do serviço.
 *    (payment_terms=30 → 1 mês de atraso, 60 → 2, 90 → 3, etc.)
 *  - Nos meses fora do intervalo faturável (`billable_months`) a empresa não emite fatura
 *    → nunca receberá receita relativa a esses meses.
 */
function buildSeries({ monthlyCost, monthlyRevenue, paymentTerms, billableMonths }) {
  const delay = Math.max(1, Math.round((paymentTerms || 30) / 30));
  const billable = Math.max(1, Math.min(12, billableMonths || 11));
  const rows = [];
  let cumulative = 0;
  for (let m = 1; m <= 12; m++) {
    // Custo pago neste mês (sempre)
    const costOut = monthlyCost;
    // Receita recebida neste mês: relativa ao serviço prestado em (m - delay), se esse mês foi faturável
    const serviceMonth = m - delay;
    const revenueIn = serviceMonth >= 1 && serviceMonth <= billable ? monthlyRevenue : 0;
    const monthBalance = revenueIn - costOut;
    cumulative += monthBalance;
    rows.push({
      month: MONTH_LABELS[m - 1],
      revenue: Math.round(revenueIn),
      cost: Math.round(costOut),
      balance: Math.round(monthBalance),
      cumulative: Math.round(cumulative),
    });
  }
  return rows;
}

export const CashflowChartWidget = ({ result, formData }) => {
  const monthlyRevenue = Number(result?.client_revenue) || 0;
  const monthlyCost = Number(result?.total_company_cost) || 0;
  const paymentTerms = Number(result?.payment_terms) || Number(formData?.payment_terms) || 30;
  const billableMonths = Number(formData?.billable_months) || 11;

  // Anual: quando não há lucro, mostrar placeholder (comportamento aprovado pelo utilizador)
  const annualProfit = (monthlyRevenue * billableMonths) - (monthlyCost * 12);
  if (!result || monthlyRevenue <= 0 || annualProfit <= 0) {
    return (
      <div className="p-4 text-xs bg-slate-50 rounded border border-slate-200 h-full flex flex-col items-center justify-center gap-1" data-testid="cashflow-chart-empty">
        <div className="text-sm font-semibold text-slate-500 uppercase">Fluidez 12M</div>
        <div className="text-xs text-slate-400">
          {result ? "Sem lucro anual — ajusta valores para visualizar a evolução." : "Aguardando cálculo…"}
        </div>
      </div>
    );
  }

  const data = buildSeries({ monthlyCost, monthlyRevenue, paymentTerms, billableMonths });
  const minCum = Math.min(...data.map((d) => d.cumulative));
  const finalCum = data[11].cumulative;

  return (
    <div className="p-3 h-full" data-testid="cashflow-chart-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-brand-600 uppercase">Fluidez 12M</div>
        <div className="text-[10px] text-slate-500">
          Prazo cliente <strong>{paymentTerms}d</strong> · faturáveis <strong>{billableMonths}m</strong>
        </div>
      </div>
      <div className="flex gap-3 mb-2 text-[10px]">
        <span className="text-slate-500">Vale mais baixo: <strong className={minCum < 0 ? "text-red-600" : "text-emerald-600"}>{formatCurrency(minCum)}</strong></span>
        <span className="text-slate-500">Ano final: <strong className="text-emerald-700">{formatCurrency(finalCum)}</strong></span>
      </div>
      <div className="w-full" style={{ height: "220px" }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip
              formatter={(value, name) => [formatCurrency(value), name === "cumulative" ? "Saldo acumulado" : name]}
              contentStyle={{ fontSize: 11 }}
            />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
            <Line
              type="monotone"
              dataKey="cumulative"
              name="Saldo acumulado"
              stroke="var(--brand-primary, #0A2828)"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CashflowChartWidget;
